import { ResourceTypeId } from '~/client/src/shared/constants/resourceTypes'
import MapViewLocationIcon from '~/client/src/shared/enums/SitemapAttributeIcon'
import SortOrder from '~/client/src/shared/enums/SortOrder'
import IHierarchyParent from '~/client/src/shared/types/IHierarchyParent'
import type { OptionGlyph } from '~/client/src/shared/types/OptionGlyph'

export const RESOURCE_NAME_KEY = 'name'

export interface IResourceRecord {
  id: string
  name: string
  code?: string | null
  stxResourceId?: string | null
  isDeleted?: boolean
  templateId?: string | null
  data: Record<string, any>
  rollups?: Record<string, any>
  root?: Record<string, any>
  infuseId?: string | null
  // Location root columns edited directly by the resource form (equipment).
  color?: string | null
  iconName?: MapViewLocationIcon | string | null
  parent?: IHierarchyParent | null
}

type ResourceFieldDataType =
  | 'number'
  | 'text'
  | 'datetime'
  | 'select'
  | 'checkbox'
  // A reference to another location (e.g. a resource's hierarchy parent); renders
  // and groups/filters by the referenced location's name via the resource-link rail.
  | 'location'

type ResourceFieldFormat =
  | 'round'
  | 'percent'
  | 'temperatureUnitAware'
  | 'relativeTime'
  | 'sensorStateLabel'

export type ResourceCardFieldKind = 'metric' | 'link' | 'text'

export type StatusBadgeVariant = 'success' | 'caution' | 'destructive' | 'muted'

export interface IResourceStatusBadge {
  variant: StatusBadgeVariant
  text: string
}

export type ResourceStatusBadgeResolver = (
  value: unknown,
) => IResourceStatusBadge | undefined

/** A card field icon taken from the field's own value rather than a static glyph. */
export type ResourceCardFieldIconKind = 'avatar' | 'companyLogo' | 'location'

export type ResourceSemanticIconKey =
  | 'company'
  | 'date'
  | 'user'
  | 'sensor'
  | 'battery'
  | 'temperature'
  | 'humidity'
  | 'telemetry'
  | 'location'
  | 'equipment'
  | 'material'

export interface IResourceRuntimeContext {
  temperatureUnit: string
  now?: Date
}

export type ResourceSemanticValueFn = (
  value: unknown,
  context?: IResourceRuntimeContext,
) => string

export type ResourceSemanticLabelFn = (
  semanticValue: string,
  context?: IResourceRuntimeContext,
) => string

export interface IResourceFieldDefinition {
  dataType: ResourceFieldDataType
  // Group by / Sort by glyph when the property type alone cannot say what the
  // field means (a text field that names the linked equipment).
  glyph?: OptionGlyph
  format?: ResourceFieldFormat[]
  card?: {
    kind?: ResourceCardFieldKind
    iconKey?: ResourceSemanticIconKey
    visible?: boolean
    isStatus?: boolean
    resolveStatusBadge?: ResourceStatusBadgeResolver
    // The linked equipment this field names, when the field labels ANOTHER record
    // rather than the card's own. Its iconName/color then replace iconKey's static
    // glyph, so the row matches how the equipment lists render that same equipment.
    resolveLinkedEquipment?: (
      record: IResourceRecord,
    ) => { color?: string | null; iconName?: string | null } | null | undefined
    /**
     * Renders the field's own value as an image — the person's avatar, or the company's
     * logo — in place of iconKey's static glyph. A marker rather than a resolver because
     * definitions import no stores: the lookup happens in the component the card mapper
     * emits, which falls back to iconKey when there is no image.
     */
    iconKind?: ResourceCardFieldIconKind
  }
  /**
   * What the field can do in a list. Declaring this block at all takes every capability
   * over from the property's own type — an omitted flag reads as false, and `table: {}`
   * is the way to say "none of them". So a field described only for a card must leave it
   * out, or it drops out of Sort by / Group by / Filter.
   */
  table?: {
    sortable?: boolean
    groupable?: boolean
    filterable?: boolean
    searchable?: boolean
    labelKey?: string
    semanticValueFn?: ResourceSemanticValueFn
    semanticLabelFn?: ResourceSemanticLabelFn
    // Materialize as a visible table column even with no sort/group/filter
    // capability — a display-only column.
    column?: boolean
    // Per-field table-cell renderer hint (overrides the type-based cell choice).
    cellType?: 'icon' | 'color'
    // The glyph a GROUP header shows when records are grouped by this field, resolved from
    // the group's key. Declared per field rather than per resource type so the icon
    // vocabulary stays where the rest of this field's list behaviour is described, and so a
    // second field wanting group artwork is one more entry rather than a new branch in the
    // shared presentation layer. Return null for a key with no artwork - the header then
    // renders its label alone.
    groupIconName?: (groupKey: string) => string | null
  }
  edit?: {
    readOnly?: boolean
  }
}

interface IResourceCardDefaults {
  titleKey: string
  subtitleKey?: string | null
  defaultDisplayPropertyKeys?: string[]
}

interface IResourceMapConfig {
  latitudeKey: string
  longitudeKey: string
  syntheticFields?: Record<string, IResourceFieldDefinition>
}

type ResourceValueSourceType = 'data' | 'root'

export interface IResourceValueRecord {
  data?: Record<string, unknown> | null
  root?: Record<string, unknown> | null
  // Top-level location root columns some resources (equipment) expose directly,
  // so value-source resolvers can read them without casting.
  color?: string | null
  iconName?: string | null
  parent?: IHierarchyParent | null
}

export type ResourceValueResolver = (record: IResourceValueRecord) => unknown

interface IResourceValueSourceDefinition {
  source: ResourceValueSourceType
  path?: string
  resolve?: ResourceValueResolver
}

export interface IResourceDefinition {
  resourceTypeId: ResourceTypeId
  fields: Record<string, IResourceFieldDefinition>
  valueSources?: Record<string, IResourceValueSourceDefinition>
  card: IResourceCardDefaults
  map?: IResourceMapConfig
  table?: {
    columnOrder?: string[]
    // Allow-list of the columns shown by default. Every other column — including
    // properties a project adds later — starts hidden but stays listed in the
    // Display panel. Omit to show everything, the pre-defaults behavior.
    defaultVisibleKeys?: string[]
    // Columns frozen (sticky-left) by default. Omit to freeze the first column only.
    defaultFrozenKeys?: string[]
    // Columns that start visible only once some record actually has a value for
    // them — StruxTag State stays out of the way until an equipment reports one.
    defaultVisibleWhenPopulatedKeys?: string[]
  }
  form?: {
    // Properties pre-selected when creating a new form, on top of the system
    // fields that are always present. Everything else starts unchecked for the
    // user to add. Omit to pre-select every editable property.
    defaultSelectedKeys?: string[]
    // Properties a new form marks mandatory. Only safe for keys `createDefaults`
    // populates, otherwise creation starts with a blank required field.
    defaultRequiredKeys?: string[]
    /**
     * Properties the form always shows and always requires, whatever the template says -
     * for a property that is part of what the resource IS, not a field a project opted
     * into. Rendered even when the template hides or omits it.
     */
    mandatoryPropertyKeys?: string[]
    // Property keys seeded from whoever is creating the record: 'currentUser'
    // fills their id, 'currentUserCompany' fills their company's id. Applies on
    // creation only, and the user can still change the value before saving.
    createDefaults?: Record<string, 'currentUser' | 'currentUserCompany'>
    // Root fields the creation form offers only when creating from Maps — Parent
    // is decided by where the resource is drawn, so it means nothing on the other
    // creation surfaces. Editing still shows them, so an assignment made in Maps
    // is never hidden: `parent` lives outside `record.data`, so the edit form's
    // assigned-field rule cannot surface it on its own.
    mapsOnlyRootFields?: string[]
    /**
     * Fields this resource derives from another one the user just edited, returned as
     * `{ fieldName: value }`. Applied on a user edit only, and only to fields the form
     * is currently showing, so a derivation never writes a value nobody can see.
     * `previousValues` are the form's values before this edit.
     */
    deriveFieldUpdates?: (
      changedFieldName: string,
      value: unknown,
      values: Record<string, unknown>,
      previousValues: Record<string, unknown>,
    ) => Record<string, unknown> | null
  }
  ui: {
    noneGroupingKey: string
    defaultGroupingKey: string | null
    /**
     * A picker grouped by this key shows its records as one tree along it, each record a
     * row under the record it points to.
     */
    pickerTreeGroupingKey?: string
    /** What a picker opens grouped by unless its field says otherwise. */
    pickerDefaultGroupingKey?: string
    /**
     * A picker's Group by, in place of every groupable field: these and no None, so the
     * picker is always grouped.
     */
    pickerGroupings?: IResourcePickerGrouping[]
    defaultSortKey: string
    defaultSortOrder: SortOrder
  }
}

export interface IResourcePickerGrouping {
  key: string
  glyph: OptionGlyph
}

export interface IResourceUiConfig {
  readOnlyPropertyNames: string[]
  noneGroupingKey: string
  defaultGroupingKey: string | null
  pickerTreeGroupingKey?: string
  pickerDefaultGroupingKey?: string
  pickerGroupings?: IResourcePickerGrouping[]
  defaultSortKey: string
  defaultSortOrder: SortOrder
}
